import type { InteractiveCanvasComponents } from '@mentimeter/question-modules-types';
import type { QuizResult } from '@mentimeter/quiz-types';
import { NonInteractiveVisualizationWrapper } from '../../shared/components/NonInteractiveVisualizationWrapper';
import { CenteringContainer } from '../../shared/components/quiz/CenteringContainer';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { QuizBarsDataHandler } from './QuizBarsDataHandler';
import type { QuizChoicesPresentationProps } from './types';

interface ViewOnlyPresentationProps extends QuizChoicesPresentationProps {
  isShowingPreviewResults: boolean;
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
  results: QuizResult[];
  shouldAnimate: boolean;
  slidePublicKey: string;
}

export const ViewOnlyPresentation = ({
  title,
  styledTitle,
  slidePublicKey,
  label,
  description,
  choices,
  textSizeOffset,
  shouldAnimate,
  results,
  isShowingPreviewResults,
  CanvasVisualizationArea,
}: ViewOnlyPresentationProps) => {
  const ConditionalWrapper =
    CanvasVisualizationArea ?? NonInteractiveVisualizationWrapper;

  if (isShowingPreviewResults) {
    return (
      <>
        <TitleSection
          title={title}
          styledTitle={styledTitle}
          label={label}
          description={description}
          fontSizeOffset={textSizeOffset}
          showTextSizeDropdown={false}
          hideAIEditingSuggestions
        />
        <CenteringContainer mt={4}>
          <QuizBarsDataHandler
            showCorrect={results.length > 0}
            rawResults={results}
            choices={choices}
            enableAnimations={shouldAnimate}
            // Force re-render when toggling preview results
            // to avoid reusing the same visualization instance, as it's not really designed to be updated after initial render
            key={`${slidePublicKey}-preview`}
          />
        </CenteringContainer>
      </>
    );
  }

  return (
    <>
      <TitleSection
        title={title}
        styledTitle={styledTitle}
        label={label}
        fontSizeOffset={textSizeOffset}
        description={description}
        showTextSizeDropdown={true}
        hideAIEditingSuggestions
      />
      <ConditionalWrapper width="100%" mt={4} flex="1">
        <CenteringContainer>
          <QuizBarsDataHandler
            showCorrect={results.length > 0}
            rawResults={results}
            choices={choices}
            enableAnimations={shouldAnimate}
            key={slidePublicKey}
          />
        </CenteringContainer>
      </ConditionalWrapper>
    </>
  );
};
